import { useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardCheck,
  ExternalLink,
  GraduationCap,
  Lock,
  LockOpen,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import useFocusTrap from "../../hooks/useFocusTrap";

const AI_ETHICS_TRAINING_URL =
  "https://hcm03.ns2cloud.com/sf/learning?destUrl=https%3a%2f%2fva%2dhcm03%2ens2cloud%2ecom%2flearning%2fuser%2fdeeplink%5fredirect%2ejsp%3flinkId%3dITEM%5fDETAILS%26componentID%3d4684359%26componentTypeID%3dVA%26revisionDate%3d1760553240000%26targetStudentSysGUID%3d%26actingAs%3d%26fromSF%3dY&company=VAHCM03";
const AI_SCRIBE_SETUP_URL =
  "https://hcm03.ns2cloud.com/sf/learning?destUrl=https%3a%2f%2fva%2dhcm03%2ens2cloud%2ecom%2flearning%2fuser%2fdeeplink%5fredirect%2ejsp%3flinkId%3dITEM%5fDETAILS%26componentID%3d4685174%26componentTypeID%3dVA%26revisionDate%3d1763410320000%26targetStudentSysGUID%3d%26actingAs%3d%26fromSF%3dY&company=VAHCM03";
const AI_DURABLE_TRAINING_URL =
  "https://hcm03.ns2cloud.com/sf/learning?destUrl=https%3a%2f%2fva-hcm03.ns2cloud.com%2flearning%2fuser%2fdeeplink_redirect.jsp%3flinkId%3dITEM_DETAILS%26componentID%3d4685419%26componentTypeID%3dVA%26revisionDate%3d1764005400000%26targetStudentSysGUID%3d%26actingAs%3d%26fromSF%3dY&company=VAHCM03";

const ATTESTATION_STATEMENTS = [
  {
    icon: GraduationCap,
    text: "I certify that I have completed the Ambient AI Scribe Ethics training.",
  },
  {
    icon: UserCheck,
    text: "I understand it is my responsibility to obtain verbal consent from the Veteran prior to using Ambient AI Scribe during a clinical visit.",
  },
  {
    icon: Lock,
    text: "I will not alter, change, or delete the automatically generated patient consent note included in the ambient AI scribe note(s).",
  },
  {
    icon: ClipboardCheck,
    text: "I understand the importance of ensuring documentation in the Veteran Electronic Health Record is reviewed following a Veteran appointment and that errors and/or discrepancies are my responsibility to correct immediately.",
  },
];

const TRAINING_ITEMS = [
  {
    id: "ethics",
    tagText: "TMS 4684359 - Prerequisite",
    tagType: "prerequisite",
    title: "Ambient AI Scribe Ethics Training",
    subtitle: "Prerequisite for all providers",
    href: AI_ETHICS_TRAINING_URL,
  },
  {
    id: "setup",
    tagText: "TMS 4685174 - Recommended",
    tagType: "recommended",
    title: "Ambient AI Scribe Setup Training",
    subtitle: "N/A for Oracle Health and Clinical Resource Hub sites",
    href: AI_SCRIBE_SETUP_URL,
  },
  {
    id: "durable",
    tagText: "TMS 4685419 - Recommended",
    tagType: "recommended",
    title: "Ambient AI Scribe Training (Durable) \u2014 Knowtex",
    subtitle: "Durable training for Knowtex providers",
    href: AI_DURABLE_TRAINING_URL,
  },
];

const TrainingItem = ({ item }) => {
  const isAvailable = Boolean(item.href);
  const isRequired = item.tagType === "prerequisite";

  return (
    <a
      href={isAvailable ? item.href : undefined}
      target={isAvailable ? "_blank" : undefined}
      rel={isAvailable ? "noreferrer noopener" : undefined}
      onClick={(event) => {
        if (!isAvailable) {
          event.preventDefault();
        }
      }}
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors ${
        isAvailable
          ? "border-[#d0ccc1] bg-[#F5F4ED] text-[#141413] hover:bg-[#efede4]"
          : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
      }`}
      aria-disabled={!isAvailable}
    >
      <div className="min-w-0 flex flex-1 items-center gap-3">
        <span
          className={`inline-flex shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${
            isRequired
              ? "bg-[#E1F5EE] text-[#135849]"
              : "bg-[#FAEEDA] text-[#775122]"
          }`}
        >
          {item.tagText}
        </span>
        <div className="min-w-0">
          <p className="mb-0 text-sm font-semibold leading-snug">{item.title}</p>
          <p className="mb-0 text-xs text-[#141413] leading-snug">{item.subtitle}</p>
          {isAvailable ? <span className="sr-only">(opens in new window)</span> : null}
        </div>
      </div>
      <ExternalLink
        aria-hidden="true"
        className={`h-4 w-4 shrink-0 ${
          isAvailable ? "text-[#141413]" : "text-slate-300"
        }`}
      />
    </a>
  );
};

export default function ProviderAttestationModal({
  isOpen,
  isLoading,
  isSaving,
  errorMessage,
  onRetry,
  onSubmit,
}) {
  const dialogRef = useRef(null);
  const firstActionRef = useRef(null);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  useFocusTrap(isOpen, dialogRef, firstActionRef, () => {});

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setHasConfirmed(false);
  }, [isOpen]);

  const canSubmit = useMemo(
    () => hasConfirmed && !isSaving,
    [hasConfirmed, isSaving]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      data-provider-attestation-modal="true"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center overflow-y-auto bg-[#0f172a]/45 px-4 py-4"
    >
      <div
        data-provider-attestation-dialog="true"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="provider-attestation-title"
        aria-describedby="provider-attestation-description"
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="bg-[#0a5a48] px-5 py-4 text-white">
          <h2
            id="provider-attestation-title"
            className="mb-0 inline-flex items-center gap-2 text-xl font-bold"
          >
            <ShieldCheck className="h-5 w-5 text-[#2FA57F]" />
            Provider attestation required
          </h2>
          <p id="provider-attestation-description" className="mb-0 mt-1 text-sm text-[#b7ddd2]">
            Please review and acknowledge the statements below before accessing the platform.
            <span className="sr-only">
              {" "}
              This dialog stays open until you submit the attestation.
            </span>
          </p>
        </div>

        <div className="min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="p-5">
              <p className="mb-0 text-sm text-slate-600">
                Checking your attestation status...
              </p>
            </div>
          ) : errorMessage ? (
            <div className="m-5 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800">
                Unable to verify attestation status.
              </p>
              <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
              <button
                ref={firstActionRef}
                type="button"
                onClick={onRetry}
                className="mt-3 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="bg-[#f8f7f3] p-5">
              <p className="mb-2 text-sm font-semibold tracking-wide text-[#141413]">
                I ATTEST THAT:
              </p>
              <div className="space-y-2">
                {ATTESTATION_STATEMENTS.map((statement) => {
                  const Icon = statement.icon;
                  return (
                    <div
                      key={statement.text}
                      className="flex items-start gap-2 rounded-lg border border-[#d0ccc1] bg-[#F5F4ED] px-3 py-2 text-sm text-[#141413]"
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#2FA57F]" />
                      <p className="mb-0">{statement.text}</p>
                    </div>
                  );
                })}
              </div>

              <div className="my-4 border-t border-[#c4c9c1]" />

              <p className="mb-1 text-sm font-semibold tracking-wide text-[#141413]">
                TRAINING
              </p>
              <div className="mb-2 flex items-center gap-2">
                <span className="inline-flex rounded bg-[#E1F5EE] px-2 py-0.5 text-[11px] font-semibold text-[#135849]">
                  Prerequisite
                </span>
                <span className="inline-flex rounded bg-[#FAEEDA] px-2 py-0.5 text-[11px] font-semibold text-[#775122]">
                  Recommended
                </span>
              </div>

              <div className="space-y-2">
                {TRAINING_ITEMS.map((item) => (
                  <TrainingItem key={item.id} item={item} />
                ))}
              </div>

              <div className="mt-4 border-t border-[#cfd8d3]" />
              <label className="mt-3 flex items-start gap-2 rounded-lg border border-[#96cdbd] bg-[#E1F5EE] px-3 py-2 text-sm text-[#135849]">
                <input
                  ref={firstActionRef}
                  type="checkbox"
                  checked={hasConfirmed}
                  onChange={(event) => setHasConfirmed(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#95b7ac] text-[#135849] focus:ring-[#135849]"
                />
                <span>
                  I indicate my agreement with all of the statements above by checking this box.
                </span>
              </label>

              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-bold transition-all duration-200 ${
                  canSubmit
                    ? "bg-[#783BFF] text-white hover:bg-[#6c2ff0] hover:shadow-md"
                    : "cursor-not-allowed border border-slate-300 bg-white text-slate-400"
                }`}
              >
                {hasConfirmed ? (
                  <LockOpen className={`h-4 w-4 ${canSubmit ? "text-white" : "text-[#7b7b7b]"}`} />
                ) : (
                  <Lock className="h-4 w-4 text-[#7b7b7b]" />
                )}
                {isSaving ? "Saving..." : "Agree and access platform"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

